import type { SurveyFormValues } from './use-medical-survey-form'

/**
 * A half-finished medical survey kept in `localStorage` so leaving the page (or
 * an accidental reload) mid-questionnaire doesn't throw the answers away. The
 * draft is dropped as soon as the survey is submitted.
 */
export interface SurveyDraft {
  answers: SurveyFormValues
  /** Question the stepper was on, so the doctor resumes where they stopped. */
  questionIndex: number
  savedAt: string
}

const PREFIX = 'medical-survey-draft'

/**
 * Drafts are scoped to the donor card (donor id) and the visit being filled, so
 * two donors — or the same donor on a later visit — never see each other's
 * answers.
 */
export const surveyDraftKey = (donorId?: string, visitId?: string) =>
  donorId && visitId ? `${PREFIX}:${donorId}:${visitId}` : null

export function readSurveyDraft(key: string | null): SurveyDraft | null {
  if (!key || typeof window === 'undefined') return null

  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null

    const { answers, questionIndex, savedAt } = parsed as Record<
      string,
      unknown
    >
    if (typeof answers !== 'object' || answers === null) return null

    return {
      answers: answers as SurveyFormValues,
      questionIndex: typeof questionIndex === 'number' ? questionIndex : 0,
      savedAt: typeof savedAt === 'string' ? savedAt : '',
    }
  } catch {
    // Unparseable or unavailable storage — behave as if there were no draft.
    return null
  }
}

/** Merges into the stored draft: answers and the question index are saved separately. */
export function writeSurveyDraft(
  key: string | null,
  patch: Partial<Pick<SurveyDraft, 'answers' | 'questionIndex'>>,
) {
  if (!key || typeof window === 'undefined') return

  const existing = readSurveyDraft(key)
  const answers = patch.answers ?? existing?.answers
  if (!answers) return

  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({
        answers,
        questionIndex: patch.questionIndex ?? existing?.questionIndex ?? 0,
        savedAt: new Date().toISOString(),
      } satisfies SurveyDraft),
    )
  } catch {
    // Storage full or blocked — the survey still works, just without a draft.
  }
}

export function clearSurveyDraft(key: string | null) {
  if (!key || typeof window === 'undefined') return

  try {
    window.localStorage.removeItem(key)
  } catch {
    // Nothing to do — the draft is ignored once the survey is completed anyway.
  }
}
